/**
 * Relative time helpers shared by the network, notification and inbox screens.
 * Invalid or missing dates render as an empty string instead of "NaN days ago".
 */

const toDate = (value: string | number | Date | null | undefined): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatShortDate = (value: string | number | Date | null | undefined, withYear = false): string => {
  const d = toDate(value);
  if (!d) return "";
  const showYear = withYear || d.getFullYear() !== new Date().getFullYear();
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: showYear ? "numeric" : undefined });
};

/** "5m ago", "3h ago", "2d ago", then "Mar 4" */
export const timeAgoShort = (value: string | number | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "";
  const diffMs = Math.max(0, Date.now() - d.getTime());
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatShortDate(d);
};

/** "Just now", "5 minutes ago", "Yesterday", "3 days ago", then "Mar 4" */
export const timeAgoLong = (value: string | number | Date | null | undefined): string => {
  const d = toDate(value);
  if (!d) return "";
  const diffMs = Math.max(0, Date.now() - d.getTime());
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }
  return formatShortDate(d);
};
